import React, { useMemo, useRef, useState } from "react";
import { Chart } from "react-chartjs-2";
import styles from "./_styles.less";
import DashboardModal from "./_DashboardModal";
import { useSelector } from "react-redux";
import { resumoTarefasSelector } from "@/store/workflow";
import { useTranslation } from "react-i18next";
import useWorkflowBarChart from "@/hooks/useWorkflowBarChart";
import { Button, Dropdown } from "antd";
import { DownOutlined, SortAscendingOutlined, SortDescendingOutlined, SwapOutlined, UpOutlined } from "@ant-design/icons";

export default function DashboardBarChart({ tipoGrafico }) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalInfo, setModalInfo] = useState({ nomeTarefa: "", tipoDataset: "", membrosListByTipoTarefa: [], colorTipo: "", chartCfg: {}, totalItens: 0 });
  const [currentSort, setCurrentSort] = useState("padrao");
  const chartRef = useRef(null);

  const resumoTiposTarefasData = useSelector(resumoTarefasSelector);
  const { buildDatasets, buildModalInfo, getTituloGrafico, getLegendVisible } = useWorkflowBarChart(resumoTiposTarefasData);

  const dataMixed = useMemo(() => {
    return {
      labels: resumoTiposTarefasData.map((item) => item.nome),
      datasets: buildDatasets(tipoGrafico),
    };
  }, [resumoTiposTarefasData, tipoGrafico]);

  const optionsMixed = {
    maintainAspectRatio: false,
    responsive: true,
    interaction: {
      mode: "point",
      intersect: true,
    },
    stacked: false,
    scales: {
      y: {
        type: "linear",
        display: true,
        position: "left",
      },
      x: {
        ticks: {
          maxRotation: 45,
          callback: (_, index) => {
            const chart = chartRef.current;
            const rawLabel = chart?.data?.labels?.[index];
            if (!rawLabel) return "";

            let labelWords = rawLabel.split(" ");
            let currentLine = "";
            let lines = [];

            for (let i = 0; i < labelWords.length; i++) {
              if ((currentLine + " " + labelWords[i]).length <= 15) {
                currentLine += labelWords[i] + " ";
              } else {
                lines.push(currentLine);
                currentLine = labelWords[i];
              }
            }
            lines.push(currentLine);
            return lines;
          },
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        display: getLegendVisible(tipoGrafico),
        labels: {
          usePointStyle: true,
        },
        onHover: (event) => {
          event.native.target.style.cursor = "pointer";
        },
        onLeave: (event) => {
          event.native.target.style.cursor = "default";
        },
      },
      title: {
        display: true,
        text: getTituloGrafico(tipoGrafico),
        padding: {
          bottom: 30,
        },
      },
      tooltip: {
        boxWidth: 20,
        boxHeight: 10,
        usePointStyle: true,
        itemSort: (a, b) => b.parsed.y - a.parsed.y,
        callbacks: {
          label: function (context) {
            const label = context.dataset.label || "";
            const value = context.parsed.y;
            const tipo = context.dataset.internalKey;
            let valueUnit;
            if (tipo.includes("tempoMedioConclusao")) {
              valueUnit = value === 1 ? t("dia") : t("dias");
              return `${label}: ${value.toFixed(1)} ${valueUnit}`;
            }
            valueUnit = value === 1 ? t("item") : t("itens");
            return `${label}: ${value} ${valueUnit}`;
          },
        },
      },
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor = elements.length ? "pointer" : "default";
    },
    onClick: (_, elements, chart) => {
      if (!elements.length) return;

      const { datasetIndex, index } = elements[0];
      const nomeTarefa = chart.data.labels[index];
      const dataset = chart.data.datasets[datasetIndex];
      const value = dataset.data[index];

      setModalInfo(buildModalInfo(nomeTarefa, dataset.internalKey, dataset.config, index, value));
      setIsModalOpen(true);
    },
  };

  function sortByTipo(dataSet) {
    if (!dataSet || !dataSet.labels) return dataSet;

    const zipped = dataSet.labels.map((label, i) => ({
      label,
      index: i,
      value: dataSet.datasets[0].data[i] ?? 0,
      colors: dataSet.datasets.map((ds) => ({
        bg: ds.backgroundColor?.[i],
        border: ds.borderColor?.[i],
      })),
    }));

    if (currentSort === "valorCrescente") {
      zipped.sort((a, b) => a.value - b.value);
    } else if (currentSort === "valorDecrescente") {
      zipped.sort((a, b) => b.value - a.value);
    } else if (currentSort === "az") {
      zipped.sort((a, b) => a.label.localeCompare(b.label));
    } else if (currentSort === "za") {
      zipped.sort((a, b) => b.label.localeCompare(a.label));
    } else {
      return dataSet;
    }

    return {
      labels: zipped.map((item) => item.label),
      datasets: dataSet.datasets.map((dataset, datasetIndex) => ({
        ...dataset,
        data: zipped.map((item) => dataset.data[item.index]),
        backgroundColor: zipped.map((item) => item.colors[datasetIndex]?.bg),
        borderColor: zipped.map((item) => item.colors[datasetIndex]?.border),
      })),
    };
  }

  const sortedData = useMemo(() => sortByTipo(dataMixed), [dataMixed, currentSort]);

  const sortItems = [
    { key: "padrao", label: t("Padrão"), icon: <SwapOutlined />, onClick: () => setCurrentSort("padrao") },
    { key: "az", label: t("A → Z"), icon: <SortAscendingOutlined />, onClick: () => setCurrentSort("az") },
    { key: "za", label: t("Z → A"), icon: <SortDescendingOutlined />, onClick: () => setCurrentSort("za") },
    { key: "valorCrescente", label: t("Crescente"), icon: <UpOutlined />, onClick: () => setCurrentSort("valorCrescente") },
    { key: "valorDecrescente", label: t("Decrescente"), icon: <DownOutlined />, onClick: () => setCurrentSort("valorDecrescente") },
  ];
  const currentSortObject = sortItems.find((item) => item.key === currentSort) || sortItems[0];

  return (
    <>
      <DashboardModal isModalOpen={isModalOpen} setModalOpen={setIsModalOpen} modalInfo={modalInfo} />
      <Dropdown menu={{ items: sortItems, selectable: true, defaultSelectedKeys: ["padrao"], selectedKeys: currentSort }} trigger={["click"]} placement="bottomRight">
        <Button icon={currentSortObject.icon} style={{ borderRadius: 6 }}>
          {currentSortObject.label === "Padrão" ? t("Ordenar") : currentSortObject.label}
        </Button>
      </Dropdown>
      <Chart type="bar" ref={chartRef} data={sortedData ?? dataMixed} options={optionsMixed} className={styles["chart-overlay"]} />
    </>
  );
}
